import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Paper,
  Tooltip,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  TableChart as ExcelIcon,
  Slideshow as PptIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Article as TextIcon,
  InsertDriveFile as FileIcon,
  PlayCircle as DemoIcon,
  TrendingUp as SalesIcon,
  School as TrainingIcon,
  Folder as FolderIcon,
  Star as StarIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import resourceService, {
  PartnerResource,
  ResourceCategory,
  formatFileSize,
} from '../services/resourceService';

// File type to icon mapping
const fileTypeIcons: Record<string, React.ReactElement> = {
  pdf: <PdfIcon sx={{ fontSize: 40, color: '#d32f2f' }} />,
  word: <DocIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
  excel: <ExcelIcon sx={{ fontSize: 40, color: '#2e7d32' }} />,
  powerpoint: <PptIcon sx={{ fontSize: 40, color: '#ed6c02' }} />,
  image: <ImageIcon sx={{ fontSize: 40, color: '#9c27b0' }} />,
  video: <VideoIcon sx={{ fontSize: 40, color: '#d32f2f' }} />,
  text: <TextIcon sx={{ fontSize: 40, color: '#757575' }} />,
  other: <FileIcon sx={{ fontSize: 40, color: '#757575' }} />,
};

// Category to icon mapping
const categoryIcons: Record<string, React.ReactElement> = {
  'demo-materials': <DemoIcon />,
  'sales-collateral': <SalesIcon />,
  'training': <TrainingIcon />,
  'technical-docs': <DocIcon />,
};

const ResourcesPage: React.FC = () => {
  useAuth(); // Ensure user is authenticated
  const [resources, setResources] = useState<PartnerResource[]>([]);
  const [categories, setCategories] = useState<ResourceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSalesAdmin, setIsSalesAdmin] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<PartnerResource | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadCategory, setUploadCategory] = useState('demo-materials');
  const [uploadTags, setUploadTags] = useState('');
  const [uploadFeatured, setUploadFeatured] = useState(false);
  const [uploadGlobal, setUploadGlobal] = useState(true);

  // Viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerResource, setViewerResource] = useState<PartnerResource | null>(null);
  const [viewerContent, setViewerContent] = useState<string>('');
  const [viewerLoading, setViewerLoading] = useState(false);

  // Fetch resources
  const fetchResources = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await resourceService.getResources({
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        search: searchTerm || undefined,
      });
      setResources(response.data.resources);
      setCategories(response.data.categories);
    } catch (err: any) {
      console.error('Error fetching resources:', err);
      setError(err.response?.data?.message || 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchTerm]);

  // Check admin role
  const checkAdminStatus = useCallback(async () => {
    try {
      const response = await resourceService.checkAdminRole();
      setIsSalesAdmin(response.data.isSalesAdmin);
    } catch (err) {
      console.error('Error checking admin role:', err);
      setIsSalesAdmin(false);
    }
  }, []);

  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchResources();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle file upload
  const handleUpload = async () => {
    if (!uploadFile || !uploadTitle) return;

    try {
      setUploadProgress(true);
      await resourceService.uploadResource({
        file: uploadFile,
        title: uploadTitle,
        description: uploadDescription,
        category: uploadCategory,
        tags: uploadTags,
        featured: uploadFeatured,
        isGlobal: uploadGlobal,
      });
      setSuccessMessage('Resource uploaded successfully');
      setUploadDialogOpen(false);
      resetUploadForm();
      fetchResources();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload resource');
    } finally {
      setUploadProgress(false);
    }
  };

  // Handle resource update
  const handleUpdate = async () => {
    if (!selectedResource) return;

    try {
      setUploadProgress(true);
      await resourceService.updateResource(selectedResource._id, {
        title: uploadTitle,
        description: uploadDescription,
        category: uploadCategory,
        tags: uploadTags,
        featured: uploadFeatured,
        isGlobal: uploadGlobal,
      });
      setSuccessMessage('Resource updated successfully');
      setEditDialogOpen(false);
      resetUploadForm();
      fetchResources();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update resource');
    } finally {
      setUploadProgress(false);
    }
  };

  // Handle resource delete
  const handleDelete = async () => {
    if (!selectedResource) return;

    try {
      await resourceService.deleteResource(selectedResource._id);
      setSuccessMessage('Resource deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedResource(null);
      fetchResources();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete resource');
    }
  };

  // Handle download
  const handleDownload = async (resource: PartnerResource) => {
    try {
      const response = await resourceService.downloadResource(resource._id);
      // Open download URL in new tab
      window.open(response.data.downloadUrl, '_blank');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to download resource');
    }
  };

  // Handle view (for PDFs, markdown, and images)
  const handleView = async (resource: PartnerResource) => {
    if (!resource.fileUrl) return;

    // For markdown files, fetch content via backend API
    if (resource.fileType === 'text' && resource.mimeType === 'text/markdown') {
      try {
        setViewerLoading(true);
        setViewerResource(resource);
        setViewerOpen(true);

        const response = await resourceService.getResourceContent(resource._id);
        setViewerContent(response.data.content);
      } catch (err: any) {
        console.error('Error fetching markdown:', err);
        setError(err.response?.data?.message || 'Failed to load document');
        setViewerOpen(false);
      } finally {
        setViewerLoading(false);
      }
      return;
    }

    // For PDFs and images, open directly in new tab (S3 presigned URLs don't embed well)
    if (resource.fileType === 'pdf' || resource.fileType === 'image') {
      window.open(resource.fileUrl, '_blank');
      return;
    }

    // For other files, open in new tab
    window.open(resource.fileUrl, '_blank');
  };

  // Close viewer
  const closeViewer = () => {
    setViewerOpen(false);
    setViewerResource(null);
    setViewerContent('');
  };

  // Reset upload form
  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadTitle('');
    setUploadDescription('');
    setUploadCategory('demo-materials');
    setUploadTags('');
    setUploadFeatured(false);
    setUploadGlobal(true);
    setSelectedResource(null);
  };

  // Open edit dialog
  const openEditDialog = (resource: PartnerResource) => {
    setSelectedResource(resource);
    setUploadTitle(resource.title);
    setUploadDescription(resource.description || '');
    setUploadCategory(resource.category);
    setUploadTags(resource.tags.join(', '));
    setUploadFeatured(resource.featured);
    setUploadGlobal(!resource.tenant);
    setEditDialogOpen(true);
    setAnchorEl(null);
  };

  // Open delete dialog
  const openDeleteDialog = (resource: PartnerResource) => {
    setSelectedResource(resource);
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, resource: PartnerResource) => {
    setAnchorEl(event.currentTarget);
    setSelectedResource(resource);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Get total count
  const totalCount = categories.reduce((sum, cat) => sum + cat.count, 0);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Partner Resource Hub
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Access demo materials, sales collateral, training documents, and more.
        </Typography>
      </Box>

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Sidebar - Categories */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Categories
            </Typography>
            <List dense>
              <ListItemButton
                selected={selectedCategory === 'all'}
                onClick={() => setSelectedCategory('all')}
              >
                <ListItemIcon>
                  <FolderIcon />
                </ListItemIcon>
                <ListItemText primary="All Resources" />
                <Badge badgeContent={totalCount} color="primary" max={999} />
              </ListItemButton>
              {categories.map((category) => (
                <ListItemButton
                  key={category.id}
                  selected={selectedCategory === category.id}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  <ListItemIcon>
                    {categoryIcons[category.id] || <FolderIcon />}
                  </ListItemIcon>
                  <ListItemText primary={category.name} />
                  <Badge badgeContent={category.count} color="primary" max={999} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={9}>
          {/* Search and Actions */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
            {isSalesAdmin && (
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={() => setUploadDialogOpen(true)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Upload Resource
              </Button>
            )}
          </Box>

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Empty State */}
          {!loading && resources.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <FolderIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No resources found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm
                  ? 'Try a different search term'
                  : 'Resources will appear here once uploaded'}
              </Typography>
            </Paper>
          )}

          {/* Resource Grid */}
          {!loading && resources.length > 0 && (
            <Grid container spacing={2}>
              {resources.map((resource) => (
                <Grid item xs={12} sm={6} lg={4} key={resource._id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      '&:hover': { boxShadow: 4 },
                    }}
                  >
                    {resource.featured && (
                      <Chip
                        icon={<StarIcon />}
                        label="Featured"
                        size="small"
                        color="warning"
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Header with icon and menu */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flexShrink: 0 }}>
                          {fileTypeIcons[resource.fileType] || fileTypeIcons.other}
                        </Box>
                        <Box sx={{ ml: 2, flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              wordBreak: 'break-word',
                            }}
                          >
                            {resource.title}
                          </Typography>
                        </Box>
                        {isSalesAdmin && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, resource)}
                            sx={{ flexShrink: 0, ml: 1 }}
                          >
                            <MoreIcon />
                          </IconButton>
                        )}
                      </Box>
                      {/* File info */}
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {formatFileSize(resource.fileSize)} • {resource.originalName}
                      </Typography>

                      {/* Description */}
                      {resource.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {resource.description}
                        </Typography>
                      )}

                      {/* Tags */}
                      {resource.tags.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                          {resource.tags.slice(0, 3).map((tag, index) => (
                            <Chip key={index} label={tag} size="small" variant="outlined" />
                          ))}
                          {resource.tags.length > 3 && (
                            <Chip
                              label={`+${resource.tags.length - 3}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      )}

                      {/* Meta info */}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {resource.downloadCount} downloads •{' '}
                        {new Date(resource.createdAt).toLocaleDateString()}
                      </Typography>
                    </CardContent>

                    <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                      {(['pdf', 'image'].includes(resource.fileType) || resource.mimeType === 'text/markdown') && (
                        <Tooltip title={resource.mimeType === 'text/markdown' ? 'View' : 'Open in new tab'}>
                          <IconButton size="small" onClick={() => handleView(resource)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleDownload(resource)}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>

      {/* Admin Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => selectedResource && openEditDialog(selectedResource)}>
          <EditIcon sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => selectedResource && openDeleteDialog(selectedResource)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => {
          setUploadDialogOpen(false);
          resetUploadForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Upload Resource
          <IconButton
            onClick={() => {
              setUploadDialogOpen(false);
              resetUploadForm();
            }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* File Upload */}
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              sx={{ py: 2 }}
            >
              {uploadFile ? uploadFile.name : 'Choose File'}
              <input
                type="file"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setUploadFile(file);
                    if (!uploadTitle) {
                      setUploadTitle(file.name.replace(/\.[^/.]+$/, ''));
                    }
                  }
                }}
              />
            </Button>

            <TextField
              label="Title"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              required
              fullWidth
            />

            <TextField
              label="Description"
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={uploadCategory}
                label="Category"
                onChange={(e) => setUploadCategory(e.target.value)}
              >
                <MenuItem value="demo-materials">Demo Materials</MenuItem>
                <MenuItem value="sales-collateral">Sales Collateral</MenuItem>
                <MenuItem value="training">Training</MenuItem>
                <MenuItem value="technical-docs">Technical Docs</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Tags (comma-separated)"
              value={uploadTags}
              onChange={(e) => setUploadTags(e.target.value)}
              placeholder="e.g., demo, sales, 2024"
              fullWidth
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={uploadFeatured}
                    onChange={(e) => setUploadFeatured(e.target.checked)}
                  />
                }
                label="Featured"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={uploadGlobal}
                    onChange={(e) => setUploadGlobal(e.target.checked)}
                  />
                }
                label="Available to all tenants"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUploadDialogOpen(false);
              resetUploadForm();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!uploadFile || !uploadTitle || uploadProgress}
            startIcon={uploadProgress ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          resetUploadForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Resource
          <IconButton
            onClick={() => {
              setEditDialogOpen(false);
              resetUploadForm();
            }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Title"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              required
              fullWidth
            />

            <TextField
              label="Description"
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={uploadCategory}
                label="Category"
                onChange={(e) => setUploadCategory(e.target.value)}
              >
                <MenuItem value="demo-materials">Demo Materials</MenuItem>
                <MenuItem value="sales-collateral">Sales Collateral</MenuItem>
                <MenuItem value="training">Training</MenuItem>
                <MenuItem value="technical-docs">Technical Docs</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Tags (comma-separated)"
              value={uploadTags}
              onChange={(e) => setUploadTags(e.target.value)}
              placeholder="e.g., demo, sales, 2024"
              fullWidth
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={uploadFeatured}
                    onChange={(e) => setUploadFeatured(e.target.checked)}
                  />
                }
                label="Featured"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={uploadGlobal}
                    onChange={(e) => setUploadGlobal(e.target.checked)}
                  />
                }
                label="Available to all tenants"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              resetUploadForm();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={!uploadTitle || uploadProgress}
            startIcon={uploadProgress ? <CircularProgress size={20} /> : <EditIcon />}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Resource</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedResource?.title}"? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Viewer Dialog */}
      <Dialog
        open={viewerOpen}
        onClose={closeViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh', maxHeight: '90vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {viewerResource?.fileType === 'pdf' ? <PdfIcon color="error" /> : <TextIcon />}
            <Typography variant="h6" component="span">
              {viewerResource?.title}
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Download">
              <IconButton onClick={() => viewerResource && handleDownload(viewerResource)}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={closeViewer}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {viewerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : (
            // Markdown Viewer
            <Box
              sx={{
                p: 3,
                overflow: 'auto',
                flex: 1,
                '& h1': { borderBottom: '1px solid #eee', pb: 1, mb: 2 },
                '& h2': { borderBottom: '1px solid #eee', pb: 1, mb: 2, mt: 3 },
                '& h3': { mt: 2, mb: 1 },
                '& pre': {
                  backgroundColor: '#f5f5f5',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: '0.875rem',
                },
                '& code': {
                  backgroundColor: '#f5f5f5',
                  px: 0.5,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontSize: '0.875rem',
                },
                '& pre code': {
                  backgroundColor: 'transparent',
                  p: 0,
                },
                '& table': {
                  borderCollapse: 'collapse',
                  width: '100%',
                  my: 2,
                },
                '& th, & td': {
                  border: '1px solid #ddd',
                  p: 1,
                  textAlign: 'left',
                },
                '& th': {
                  backgroundColor: '#f5f5f5',
                  fontWeight: 'bold',
                },
                '& blockquote': {
                  borderLeft: '4px solid #ddd',
                  pl: 2,
                  ml: 0,
                  color: '#666',
                  fontStyle: 'italic',
                },
                '& ul, & ol': {
                  pl: 3,
                },
                '& li': {
                  mb: 0.5,
                },
                '& a': {
                  color: 'primary.main',
                },
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                },
                '& hr': {
                  border: 'none',
                  borderTop: '1px solid #eee',
                  my: 2,
                },
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {viewerContent}
              </ReactMarkdown>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ResourcesPage;
